import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import memberService from '../../services/memberService';
import {
  Container, Paper, Typography, Grid, TextField, Button, FormControl,
  InputLabel, Select, MenuItem, FormHelperText, Box, Divider,
  CircularProgress, Alert, Stepper, Step, StepLabel,
  FormControlLabel, Switch, Snackbar
} from '@mui/material';
import {
  Person as PersonIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ChevronRight as ChevronRightIcon,
  Check as CheckIcon,
  Add as AddIcon
} from '@mui/icons-material';

const AddMemberPage = () => {
  const { register, handleSubmit, reset, control, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      membershipType: 'regular',
      status: 'active',
      directoryVisible: true,
      generatePassword: false,
      password: '',
      confirmPassword: '',
      role: 'member'
    }
  });
  
  const { tenant } = useTenant();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [customFields, setCustomFields] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [previewData, setPreviewData] = useState(null);

  const password = watch('password');
  const generatePassword = watch('generatePassword');
  const userRole = watch('role');

  useEffect(() => {
    if (tenant && tenant.settings && tenant.settings.membershipFields) {
      setCustomFields(tenant.settings.membershipFields);
    }
  }, [tenant]);

  const steps = ['Personal Information', 'Membership Details', 'Review & Create'];

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate first step fields
      if (!watch('firstName') || !watch('lastName') || !watch('email')) {
        setError('Please fill out all required fields before proceeding.');
        return;
      }
      if (errors.email) {
        setError('Please enter a valid email address.');
        return;
      }
    }
    
    if (activeStep === 1) {
      const memberData = watch();
      setPreviewData(memberData);
    }
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setError(null);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError(null);
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);
      
      // Process directory visibility setting
      const processedData = {
        ...data,
        directoryVisible: data.directoryVisible === true || data.directoryVisible === "true",
        tenantId: tenant?._id
      };
      
      // Remove confirm password from the data sent to API
      delete processedData.confirmPassword;
      
      // If generate password is checked, remove the password field so backend generates one
      if (processedData.generatePassword) {
        delete processedData.password;
      }
      
      // Remove the generatePassword field as it's just for UI control
      delete processedData.generatePassword;
      
      // Make sure role is explicitly included in the request
      // This is critical - confirm that the role is being sent
      if (data.role) {
        console.log(`Creating member with role: ${data.role}`);
        processedData.role = data.role;
        // Some APIs might need the role on the user object
        processedData.userRole = data.role;
      }
      
      // Format custom fields
      if (customFields.length > 0) {
        const formattedCustomFields = {};
        customFields.forEach(field => {
          if (processedData[field.name]) {
            formattedCustomFields[field.name] = processedData[field.name];
            delete processedData[field.name];
          }
        });
        
        if (Object.keys(formattedCustomFields).length > 0) {
          processedData.customFields = formattedCustomFields;
        }
      }
      
      console.log('Creating member with data:', processedData);
      
      const response = await memberService.createMember(processedData);
      
      setSuccess(true);
      setSnackbarMessage('Member created successfully!');
      setSnackbarOpen(true);
      
      // Redirect to the member list after a short delay
      setTimeout(() => {
        navigate('/admin/members');
      }, 2000);
      
    } catch (error) {
      console.error('Error creating member:', error);
      setError(error.message || 'Failed to create member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderPersonalInfo = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="First Name"
          {...register('firstName', { required: 'First name is required' })}
          error={!!errors.firstName}
          helperText={errors.firstName?.message}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Last Name"
          {...register('lastName', { required: 'Last name is required' })}
          error={!!errors.lastName}
          helperText={errors.lastName?.message}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          {...register('email', { 
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address'
            }
          })}
          error={!!errors.email}
          helperText={errors.email?.message}
        />
      </Grid>
      
      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel id="role-label">User Role</InputLabel>
          <Controller
            name="role"
            control={control}
            defaultValue="member"
            render={({ field }) => (
              <Select
                labelId="role-label"
                label="User Role"
                {...field}
              >
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="staff">Staff</MenuItem>
                {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
                  <MenuItem value="admin">Admin</MenuItem>
                )}
                {currentUser?.role === 'superadmin' && (
                  <MenuItem value="superadmin">Superadmin</MenuItem>
                )}
              </Select>
            )}
          />
          <FormHelperText>
            {userRole === 'staff' ? 
              'Staff members can view and manage content but have limited administrative abilities.' : 
              userRole === 'admin' ? 
                'Admins have full management permissions for this organization.' : 
                'Regular members have standard access to organization features.'}
          </FormHelperText>
        </FormControl>
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Password"
          type="password"
          {...register('password', { 
            required: 'Password is required',
            minLength: {
              value: 8,
              message: 'Password must be at least 8 characters'
            }
          })}
          error={!!errors.password}
          helperText={errors.password?.message}
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Confirm Password"
          type="password"
          {...register('confirmPassword', { 
            required: 'Please confirm your password',
            validate: value => value === password || 'Passwords do not match'
          })}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
        />
      </Grid>
    </Grid>
  );

  const renderMembershipDetails = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel id="membership-type-label">Membership Type</InputLabel>
          <Controller
            name="membershipType"
            control={control}
            defaultValue="regular"
            render={({ field }) => (
              <Select
                labelId="membership-type-label"
                label="Membership Type"
                {...field}
              >
                <MenuItem value="regular">Regular</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
                <MenuItem value="lifetime">Lifetime</MenuItem>
              </Select>
            )}
          />
        </FormControl>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel id="status-label">Status</InputLabel>
          <Controller
            name="status"
            control={control}
            defaultValue="active"
            render={({ field }) => (
              <Select
                labelId="status-label"
                label="Status"
                {...field}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            )}
          />
        </FormControl>
      </Grid>
      
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Controller
              name="directoryVisible"
              control={control}
              defaultValue={true}
              render={({ field }) => (
                <Switch 
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              )}
            />
          }
          label="Visible in Member Directory"
        />
        <FormHelperText>
          When enabled, this member will be visible to other members in the directory
        </FormHelperText>
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Notes"
          multiline
          rows={4}
          {...register('notes')}
        />
      </Grid>
      
      {customFields.map((field) => (
        <Grid item xs={12} key={field.name}>
          <TextField
            fullWidth
            label={field.label || field.name}
            {...register(field.name)}
          />
        </Grid>
      ))}
    </Grid>
  );

  const renderPreview = () => (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Personal Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary">Name</Typography>
            <Typography>{watch('firstName')} {watch('lastName')}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary">Email</Typography>
            <Typography>{watch('email')}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary">User Role</Typography>
            <Typography sx={{ textTransform: 'capitalize' }}>{watch('role')}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary">Password</Typography>
            <Typography>
              ********
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Membership Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary">Membership Type</Typography>
            <Typography sx={{ textTransform: 'capitalize' }}>{watch('membershipType')}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
            <Typography sx={{ textTransform: 'capitalize' }}>{watch('status')}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary">Membership Number</Typography>
            <Typography>Will be generated automatically</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary">Directory Visibility</Typography>
            <Typography>{watch('directoryVisible') ? 'Visible' : 'Hidden'}</Typography>
          </Grid>
          
          {watch('notes') && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
              <Typography>{watch('notes')}</Typography>
            </Grid>
          )}
          
          {customFields.map((field) => 
            watch(field.name) && (
              <Grid item xs={6} key={field.name}>
                <Typography variant="subtitle2" color="text.secondary">{field.label || field.name}</Typography>
                <Typography>{watch(field.name)}</Typography>
              </Grid>
            )
          )}
        </Grid>
      </Paper>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4,  
          p: 3, 
          borderRadius: 2,
          background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
            <AddIcon sx={{ mr: 1 }} /> Add New Member
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8 }}>
            Create a new member account in {tenant?.name || 'your organization'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/members')}
          sx={{ 
            color: 'white', 
            borderColor: 'rgba(255, 255, 255, 0.5)',
            '&:hover': {
              borderColor: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          Back to Members
        </Button>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/admin/members')}>
              View All Members
            </Button>
          }
        >
          Member created successfully!
        </Alert>
      )}

      <Paper 
        elevation={2} 
        sx={{ 
          p: 4, 
          borderRadius: 2,
          borderTop: '4px solid #3f51b5' 
        }}
      >
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 3 }}>
          {activeStep === 0 && renderPersonalInfo()}
          {activeStep === 1 && renderMembershipDetails()}
          {activeStep === 2 && renderPreview()}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              sx={{ mr: 1 }}
            >
              Back
            </Button>
          )}
          
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ChevronRightIcon />}
              sx={{
                background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
              sx={{
                background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
                boxShadow: '0 4px 6px rgba(76, 175, 80, 0.25)',
              }}
            >
              {loading ? 'Creating...' : 'Create Member'}
            </Button>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default AddMemberPage;
